import React, { useRef, useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography } from '@components/atoms/Typography';
import { useTranslation } from 'react-i18next';
import { useRegistrationFirstStep } from '@services/useRegistrationFirstStep';
import { useData } from '@hooks/useData';
import { Form } from '@components/atoms/Form';
import { useNotifications } from '@hooks/useNotifications';
import { Draft } from './../../../context/dataContext';
import { useNavigate } from 'react-router-dom';
import { loadFromLocalStorage } from '@utils/storage';
import { Role, PersonalInfoData, PersonalInfoDataErrors, Step1Data } from '@types';

interface FirstStepData {
    uuid: string;
    current_step: number;
    step_1: Step1Data;
    otp: { validated: boolean };
}

interface DataContextType {
    draft?: Draft | null;
    firstStep?: { data?: FirstStepData };
    setFirstStep?: (data: { data: FirstStepData }) => void;
    selectedRole?: { id: number };
    otpStep?: { verified: boolean; timestamp: number };
}

export const PersonalInfoStep: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const formRef = useRef<HTMLFormElement>(null);
    const { firstStep, setFirstStep } = useData() as DataContextType;
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const { setRegister } = useData();
    const { showDefaultNotification } = useNotifications();
    const { request: requestRegisterFirstStep, error, validationErrors, lastResponseTime } = useRegistrationFirstStep();
    const [errors, setErrors] = useState<PersonalInfoDataErrors>({
        name: [],
        email: [],
        phone: [],
        address: [],
        license_number: [],
    });

    useEffect(() => {
        const stored = loadFromLocalStorage('selectedRole');

        if (stored) {
            try {
                const roleObj = (typeof stored === 'string' ? JSON.parse(stored) : stored) as Role;
                setSelectedRole(roleObj);
            } catch (err) {
                console.error('Failed to parse stored role', err);
            }
        }
    }, []);

    useEffect(() => {
        setRegister(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // handles stories
    const context = useOutletContext<{
        onNext?: (skipOtp?: boolean) => void;
        onBack?: () => void;
    } | null>();
    const onNext = context?.onNext || (() => {});
    const onBack = () => {
        navigate('/select-account-type');
    };

    const [localData, setLocalData] = useState<PersonalInfoData>({
        uuid: firstStep?.data?.uuid || '',
        name: firstStep?.data?.step_1?.name || '',
        email: firstStep?.data?.step_1?.email || '',
        provider: firstStep?.data?.step_1?.provider || 'normal',
        phone: firstStep?.data?.step_1?.phone_number || '',
        address: firstStep?.data?.step_1?.address || '',
        license_number: firstStep?.data?.step_1?.license_number || '',
    });

    useEffect(() => {
        if (firstStep?.data?.step_1) {
            setLocalData({
                uuid: firstStep.data.uuid || '',
                name: firstStep.data.step_1.name || '',
                email: firstStep.data.step_1.email || '',
                provider: firstStep.data.step_1.provider || 'normal',
                phone: firstStep.data.step_1.phone_number || '',
                address: firstStep.data.step_1.address || '',
                license_number: firstStep.data.step_1.license_number || '',
            });
        }
    }, [firstStep]);

    useEffect(() => {
        if (lastResponseTime) {
            if (validationErrors) {
                setErrors({
                    name: validationErrors['name'] || [],
                    email: validationErrors['email'] || [],
                    phone: validationErrors['phone_number'] || [],
                    address: validationErrors['address'] || [],
                    license_number: validationErrors['license_number'] || [],
                });
            } else if (error) {
                showDefaultNotification({ type: 'error', message: error });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const translations = {
        title: t('personal-info-form.title'),
        prev: t('navigation.prev'),
        next: t('navigation.next'),
        nameLabel: t('personal-info-form.name.label'),
        emailLabel: t('personal-info-form.email.label'),
        phoneLabel: t('personal-info-form.phone.label'),
        addressLabel: t('personal-info-form.address.label'),
        licenseNumberLabel: t('personal-info-form.license_number.label'),
        nameRequiredError: t('personal-info-form.name.required-error'),
        emailRequiredError: t('personal-info-form.email.required-error'),
        phoneRequiredError: t('personal-info-form.phone.required-error'),
        addressRequiredError: t('personal-info-form.address.required-error'),
        licenseNumberRequiredError: t('personal-info-form.license_number.required-error'),
    };

    const handleChange = (field: keyof PersonalInfoData, value: string) => {
        const updated: PersonalInfoData = { ...localData, [field]: value };
        setLocalData(updated);

        if (setFirstStep && firstStep?.data?.step_1) {
            setFirstStep({
                data: {
                    ...firstStep.data,
                    step_1: {
                        ...firstStep.data.step_1,
                        [field === 'phone' ? 'phone_number' : field]: value,
                    },
                },
            });
        }
    };

    const validateForm = (): boolean => {
        const newErrors: PersonalInfoDataErrors = {
            name: localData.name ? [] : [translations.nameRequiredError],
            email: localData.email ? [] : [translations.emailRequiredError],
            phone: localData.phone ? [] : [translations.phoneRequiredError],
            address: localData.address ? [] : [translations.addressRequiredError],
            license_number:
                selectedRole?.name === 'Driver' && !localData.license_number ? [translations.licenseNumberRequiredError] : [],
        };

        if (localData.email !== '' && !isValidEmail(localData.email)) {
            newErrors.email.push(t('personal-info-form.email.invalid-error'));
        }
        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some((fieldErrors) => fieldErrors.length > 0);

        return hasErrors ? false : true;
    };

    const submitForm = async () => {
        if (!validateForm()) {
            return;
        }

        requestRegisterFirstStep(
            {
                name: localData.name,
                email: localData.email,
                provider: localData.provider as 'normal' | 'google',
                phone_number: localData.phone,
                user_type_id: selectedRole?.id as 2 | 3,
                address: localData.address,
                license_number: localData.license_number,
            },
            { onNext }
        );
    };

    return (
        <Form formRef={formRef} onSubmit={submitForm} classes="d-flex flex-column gap-2">
            <Typography align="center" fontSize="16" fontWeight="400" color="muted">
                {translations.title}
            </Typography>

            <Input
                data-testid="name-input"
                variant={InputVariants.labeledInput}
                type={InputTypes.text}
                label={`${translations.nameLabel}*`}
                value={localData.name}
                onChange={(value) => handleChange('name', value)}
                state={errors.name.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.name}
            />
            <Input
                variant={InputVariants.labeledInput}
                type={InputTypes.email}
                label={`${translations.emailLabel}*`}
                value={localData.email}
                onChange={(value) => handleChange('email', value)}
                state={errors.email.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.email}
            />
            <Input
                variant={InputVariants.labeledInput}
                type={InputTypes.tel}
                label={`${translations.phoneLabel}*`}
                value={localData.phone}
                onChange={(value) => handleChange('phone', value)}
                state={errors.phone.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.phone}
            />
            <Input
                variant={InputVariants.labeledInput}
                type={InputTypes.textarea}
                label={`${translations.addressLabel}*`}
                value={localData.address}
                onChange={(value) => handleChange('address', value)}
                state={errors.address.length > 0 ? InputStates.error : undefined}
                errorMessages={errors.address}
            />

            <>
                {selectedRole?.name === 'Driver' && (
                    <Input
                        variant={InputVariants.labeledInput}
                        type={InputTypes.text}
                        label={`${translations.licenseNumberLabel}*`}
                        value={localData.license_number}
                        onChange={(value) => handleChange('license_number', value)}
                        state={errors.license_number.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.license_number}
                    />
                )}
            </>

            <Button onClick={onBack} label={translations.prev} mode={ButtonMode.primary} outline />
            <Button label={translations.next} mode={ButtonMode.primary} onClick={submitForm} />
        </Form>
    );
};
